import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Avatar,
  Modal,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import {
  Close,
  Person,
  Phone,
  CalendarToday,
  GetApp,
  GridView,
  ViewList
} from "@mui/icons-material";
import { CSVLink } from "react-csv";
import React, { useEffect, useState } from "react";
import { getPrimePinReportByMember } from "src/api/primestockapi";
import { Routes } from "src/constant/api_url";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1100,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  p: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh"
};

export default function PinReportModal({ rowData, open, onClose }) {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [exportData, setExportData] = useState([]);
  const csvLinkRef = React.useRef();

  useEffect(() => {
    if (open && rowData.m_id && rowData.p_a_p_id) {
      const run = async () => {
        setLoading(true);
        try {
          const resp = await getPrimePinReportByMember({
            m_id: rowData.m_id,
            p_a_p_id: rowData.p_a_p_id
          });
          if (resp?.status == 1) {
            setReportData(resp.data?.referral_list || []);
          } else {
            setReportData([]);
          }
        } catch (error) {
          console.error("Error fetching pin report:", error);
          setReportData([]);
        } finally {
          setLoading(false);
        }
      };
      run();
    }
  }, [open, rowData]);

  const handleExportCSV = () => {
    setExportData(reportData);
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 100);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ p: 2.5, bgcolor: "#034766", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Avatar
                src={rowData?.photo ? `${Routes.API_HOSTNAME}images/member/${rowData.photo}` : undefined}
                alt={rowData?.m_name}
                sx={{ width: 36, height: 36, border: "2px solid rgba(255,255,255,0.3)" }}
              >
                {!rowData?.photo && <Person />}
              </Avatar>
              {(rowData?.m_verify_status === 1 || rowData?.m_verify_status === 2 || rowData?.m_verify_status === 3) && (
                <Box
                  component="img"
                  src={rowData?.m_verify_status === 1 ? "/static/icons/verified.png" : rowData?.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified_orange.png"}
                  alt="Verified"
                  sx={{ width: 16, height: 16, position: "absolute", top: 0, right: 0, transform: "translate(30%, -30%)" }}
                />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
              Pin Report - {rowData?.p_name} - {rowData?.m_name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: "8px",
                "& .MuiToggleButton-root": {
                  color: "#fff",
                  border: "none",
                  px: 1.5,
                  "&.Mui-selected": {
                    bgcolor: "#fff",
                    color: "#034766",
                    "&:hover": { bgcolor: "#fff" }
                  }
                }
              }}
            >
              <ToggleButton value="grid">
                <GridView sx={{ fontSize: "1.2rem" }} />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewList sx={{ fontSize: "1.2rem" }} />
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={onClose} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, overflowY: "auto", flexGrow: 1, bgcolor: "#F1F5F9" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={10}>
              <CircularProgress sx={{ color: "#034766" }} />
            </Box>
          ) : reportData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                No report data found for this member.
              </Typography>
            </Box>
          ) : viewMode === "grid" ? (
            <Grid container spacing={2}>
              {reportData.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)",
                    bgcolor: "#fff",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)"
                    }
                  }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box sx={{ position: "relative", display: "inline-block" }}>
                        <Avatar
                          src={item?.photo ? `${Routes.API_HOSTNAME}images/member/${item.photo}` : undefined}
                          alt={item?.m_name}
                          sx={{ width: 36, height: 36, border: "2px solid rgba(255,255,255,0.3)" }}
                        >
                          {!item?.photo && <Person />}
                        </Avatar>
                        {(item?.m_verify_status === 1 || item?.m_verify_status === 2 || item?.m_verify_status === 3) && (
                          <Box
                            component="img"
                            src={item?.m_verify_status === 1 ? "/static/icons/verified.png" : item?.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified_orange.png"}
                            alt="Verified"
                            sx={{ width: 16, height: 16, position: "absolute", top: 0, right: 0, transform: "translate(30%, -30%)" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ color: "#64748B", fontSize: "0.8rem", fontWeight: 600 }}>
                        Sr No. {index + 1}
                      </Typography>
                    </Box>

                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1E293B", lineBreak: "anywhere" }}>
                      {item.m_name || "N/A"}
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />

                    <Stack spacing={1.5} sx={{ mt: "auto" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Phone sx={{ fontSize: "0.9rem", color: "#94A3B8" }} />
                          <Typography sx={{ color: "#64748B", fontSize: "0.85rem", fontWeight: 500 }}>Mobile</Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#334155", textAlign: "right" }}>{item.mobile_no || "-"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Person sx={{ fontSize: "0.9rem", color: "#94A3B8" }} />
                          <Typography sx={{ color: "#64748B", fontSize: "0.85rem", fontWeight: 500 }}>Email</Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#334155", textAlign: "right" }}>{item.email || "-"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CalendarToday sx={{ fontSize: "0.9rem", color: "#94A3B8" }} />
                          <Typography sx={{ color: "#64748B", fontSize: "0.85rem", fontWeight: 500 }}>Joining Date</Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#334155", textAlign: "right" }}>{item.joining_date || "-"}</Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: "1px solid #E2E8F0" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700, width: "80px" }}>Sr No</TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>Member Name</TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>Mobile</TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>Joining Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell width={60}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{item.m_name || "-"}</TableCell>
                      <TableCell>{item.mobile_no || "-"}</TableCell>
                      <TableCell>{item.email || "-"}</TableCell>
                      <TableCell>{item.joining_date || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "flex-end", gap: 2, borderTop: "1px solid #E2E8F0", bgcolor: "#fff" }}>
          <Button onClick={onClose} sx={{ color: "#64748B", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#F1F5F9" } }}>
            Cancel
          </Button>
          <CSVLink
            data={exportData}
            headers={[
              { label: "Sr No", key: "sr_no" },
              { label: "Member Name", key: "m_name" },
              { label: "Mobile", key: "mobile_no" },
              { label: "Email", key: "email" },
              { label: "Joining Date", key: "joining_date" }
            ]}
            filename={`Pin_Report_${rowData?.m_name}.csv`}
            ref={csvLinkRef}
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              startIcon={<GetApp />}
              sx={{
                bgcolor: "#034766",
                "&:hover": { bgcolor: "#02364d" },
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgba(3,71,102,0.3)"
              }}
              onClick={handleExportCSV}
            >
              Export Report
            </Button>
          </CSVLink>
        </Box>
      </Box>
    </Modal>
  );
}
